import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import Input from 'dpl/form/components/Input';
import FormContext from 'dpl/shared/form/utils/context';
import useForm from 'dpl/shared/form/hooks/useForm';
import useCurrentUser from 'dpl/common/hooks/useCurrentUser';
import { TOS_PATH, PRIVACY_PATH } from 'dpl/shared/constants/urls';
import { CONSTRAINT_TYPES } from 'dpl/shared/validations/constraints';
import { createUserMutationDefinition } from 'dpl/shared/buyer/utils/queryDefinitions';
import useResourceMutation from 'dpl/shared/fetching/hooks/useResourceMutation';
import { USER_TYPES } from 'dpl/shared/constants/shared';
import { sendAnalyticsIdentify } from 'dpl/util/analytics';

import AdditionalInfoForm from './AdditionalInfoForm';

export default function SignupForm({
  buttonText,
  headerText,
  subHeaderText,
  onSuccessfulAuth
}) {
  const currentUser = useCurrentUser();
  const { mutateAsync: signup } = useResourceMutation(
    createUserMutationDefinition
  );
  const [displayAdditionalInfoForm, setDisplayAdditionalInfoForm] =
    useState(false);
  const { isSubmitting, handleSubmission, contextValue, isValid, reset } =
    useForm({
      onSubmit: (formState, additionalAttributes) =>
        signup({
          body: {
            user_type: USER_TYPES.BUYER,
            user: {
              ...formState,
              ...additionalAttributes
            }
          }
        }),
      mapFormStateToValidationSchema: () => ({
        email: CONSTRAINT_TYPES.EMAIL_VALID,
        password: CONSTRAINT_TYPES.PASSWORD
      })
    });

  useEffect(() => {
    reset();
  }, []);

  function handleSubmit(e) {
    e.preventDefault();
    setDisplayAdditionalInfoForm(true);
  }

  async function createUser(userAttributes) {
    const ret = await handleSubmission(userAttributes);

    if (ret instanceof Error) {
      return false;
    }

    sendAnalyticsIdentify({ userId: ret.data.user.id });

    return ret.data.user;
  }

  async function handleSuccessfulSubmission() {
    await currentUser.fetch();
    return onSuccessfulAuth();
  }

  if (displayAdditionalInfoForm) {
    return (
      <AdditionalInfoForm
        createUserFn={createUser}
        onSuccessfulSubmission={handleSuccessfulSubmission}
      />
    );
  }

  return (
    <div className="BuyerAccountSignupForm">
      <h5 className="tc mb2">{headerText}</h5>
      {subHeaderText && <p className="tc mb8 mid-gray">{subHeaderText}</p>}
      <FormContext.Provider value={contextValue}>
        <form onSubmit={handleSubmit}>
          <div className="mb6">
            <Input
              name="email"
              label="Email Address"
              placeholder="jane@highpeaksfarm.com"
              autoCapitalize="none"
            />
          </div>

          <div className="mb10">
            <Input
              name="password"
              label="Password"
              type="password"
              placeholder="6+ characters"
              autoCapitalize="none"
            />
          </div>

          <div className="mb6">
            <button
              data-test-id="signup_submit"
              type="submit"
              disabled={isSubmitting || !isValid}
              className="button button--primary pv2 w-100"
            >
              {buttonText}
            </button>
          </div>

          <div className="mb6">
            <p className="f2 mid-gray tc">
              By continuing, I agree to Good Dog&apos;s&nbsp;
              <a
                className="underline"
                href={TOS_PATH}
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms of Service
              </a>
              &nbsp;and{' '}
              <a
                className="underline"
                href={PRIVACY_PATH}
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>
              .
            </p>
          </div>
        </form>
      </FormContext.Provider>
    </div>
  );
}

SignupForm.propTypes = {
  buttonText: PropTypes.string.isRequired,
  headerText: PropTypes.string.isRequired,
  subHeaderText: PropTypes.string,
  onSuccessfulAuth: PropTypes.func.isRequired
};

SignupForm.defaultProps = {
  subHeaderText: null
};
